import { ColumnMapping, ExcelProcessorConfig, HeaderConfig, ParsedExcelData, ValidationError } from '@/lib/excel';
import { NhanVienInput } from '@/types/schemas';

const HEADERS = {
  EMPLOYEE_CODE: 'Mã nhân viên*',
  FULL_NAME: 'Họ và Tên*',
  POSITION: 'Chức vụ',
  DEPARTMENT_CODE: 'Bộ phận',
  GENDER: 'Giới tính*',
  BIRTH_DATE: 'Ngày sinh',
  BIRTH_PLACE: 'Nơi sinh',
  ADDRESS: 'Địa chỉ thường trú',
  PHONE: 'Điện thoại',
  EMAIL: 'Email',
  TAX_CODE: 'Mã số thuế TNCN',
  ID_NUMBER: 'Số CMND',
  ID_ISSUE_DATE: 'Ngày cấp',
  ID_ISSUE_PLACE: 'Nơi cấp',
  BANK_ACCOUNT: 'Số thẻ/Ngân hàng',
  BANK_NAME: 'Tên ngân hàng',
  IS_SALES_REP: 'Nhân viên bán hàng',
  IS_PURCHASING: 'Nhân viên mua hàng',
  IS_DEBT_ADVANCE: 'Công nợ/tạm ứng'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.EMPLOYEE_CODE, key: 'ma_nhan_vien', type: 'main' },
  { header: HEADERS.FULL_NAME, key: 'ho_ten_nhan_vien', type: 'main' },
  { header: HEADERS.POSITION, key: 'chuc_vu', type: 'main' },
  { header: HEADERS.DEPARTMENT_CODE, key: 'ma_bp', type: 'main' },
  { header: HEADERS.GENDER, key: 'gioi_tinh', type: 'main' },
  { header: HEADERS.BIRTH_DATE, key: 'ngay_sinh', type: 'main' },
  { header: HEADERS.BIRTH_PLACE, key: 'noi_sinh', type: 'main' },
  { header: HEADERS.ADDRESS, key: 'dia_chi', type: 'main' },
  { header: HEADERS.PHONE, key: 'dien_thoai', type: 'main' },
  { header: HEADERS.EMAIL, key: 'email', type: 'main' },
  { header: HEADERS.TAX_CODE, key: 'ma_so_thue_tncn', type: 'main' },
  { header: HEADERS.ID_NUMBER, key: 'so_cmnd', type: 'main' },
  { header: HEADERS.ID_ISSUE_DATE, key: 'ngay_hieu_luc_cmnd', type: 'main' },
  { header: HEADERS.ID_ISSUE_PLACE, key: 'noi_cap_cmnd', type: 'main' },
  { header: HEADERS.BANK_ACCOUNT, key: 'so_the', type: 'main' },
  { header: HEADERS.BANK_NAME, key: 'ten_ngan_hang', type: 'main' },
  { header: HEADERS.IS_SALES_REP, key: 'nhan_vien_ban_hang', type: 'main' },
  { header: HEADERS.IS_PURCHASING, key: 'nhan_vien_mua_hang', type: 'main' },
  { header: HEADERS.IS_DEBT_ADVANCE, key: 'cong_no_tam_ung', type: 'main' }
];

const parse = {
  string(val: unknown): string | null {
    if (val == null || String(val).trim() === '') return null;
    return String(val).trim();
  },

  number(val: unknown, def = 0): number {
    if (val == null || String(val).trim() === '') return def;
    const n = parseFloat(String(val));
    return Number.isNaN(n) ? def : Math.round(n * 100) / 100;
  },

  boolean(val: unknown, def = false): boolean {
    if (val == null || String(val).trim() === '') return def;
    return ['1', 'true', 'TRUE', 'True'].includes(String(val));
  },

  gender(val: unknown): string | null {
    if (val == null || String(val).trim() === '') return null;
    const str = String(val).trim();
    if (['1', '2', '3'].includes(str)) return str;
    const lower = str.toLowerCase();
    if (['nam', 'male', '1'].includes(lower)) return '1';
    if (['nữ', 'nu', 'female', '2'].includes(lower)) return '2';
    if (['khác', 'other', '3'].includes(lower)) return '3';
    return null;
  },

  date(val: unknown): string | null {
    if (!val) return null;

    if (val instanceof Date) {
      const date = val;
      return isNaN(date.getTime())
        ? null
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
            2,
            '0'
          )}`;
    }

    const str = String(val).trim();

    const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const [, d, mo, y] = m;
      return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

    const date = new Date(str);
    return isNaN(date.getTime())
      ? null
      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
          2,
          '0'
        )}`;
  }
};

type ExcelFieldRows = Record<string, unknown[]>;
type UUIDLookupResponse = { main?: Record<string, Array<string | null | undefined>> };

const UUID_FIELDS = {
  main: ['ma_bp']
};

const getUUID = (uuid: unknown, field: string, i: number): string => {
  const value = (uuid as UUIDLookupResponse | null)?.main?.[field]?.[i];
  return value ? String(value) : '';
};

const UUIDPayload = (data: ParsedExcelData) => ({
  main: Object.fromEntries(
    UUID_FIELDS.main.map(f => [
      f,
      (data.main?.[f] || []).map(v => (v == null || String(v).trim() === '' ? '' : String(v).trim()))
    ])
  )
});

const getFieldName = (key: string) =>
  HEADER_CONFIG.find(c => c.key === key)
    ?.header?.replace('*', '')
    .trim() || key;

const ValidateUUID = (response: unknown, columnMapping: Record<string, ColumnMapping>): ValidationError[] => {
  const main = (response as UUIDLookupResponse | null)?.main;
  if (!main) return [];

  const errors: ValidationError[] = [];

  Object.entries(main).forEach(([field, values]) => {
    if (!Array.isArray(values)) return;

    const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === 'main');
    if (!mapping) return;

    values.forEach((val, i) => {
      if (val === null) {
        errors.push({
          row: i + 2,
          field,
          section: 'main',
          columnIndex: mapping.index,
          message: `${getFieldName(field)} không hợp lệ`
        });
      }
    });
  });

  return errors;
};

type ImportBody = NhanVienInput;

const REQUIRED_FIELDS = ['ma_nhan_vien', 'ho_ten_nhan_vien'] as const;

const hasRequired = (main: ExcelFieldRows, i: number) =>
  REQUIRED_FIELDS.every(f => main[f]?.[i] != null && String(main[f][i]).trim() !== '');

const TransformToBodies = (rows: unknown[][], parsed: ParsedExcelData, uuid: unknown): ImportBody[] => {
  const main = (parsed.main ?? {}) as ExcelFieldRows;

  return rows.reduce<ImportBody[]>((acc, _, i) => {
    if (!hasRequired(main, i)) return acc;

    const ma_nhan_vien = parse.string(main.ma_nhan_vien[i]);
    const ho_ten_nhan_vien = parse.string(main.ho_ten_nhan_vien[i]);
    if (!ma_nhan_vien || !ho_ten_nhan_vien) return acc;

    const gioi_tinh = parse.gender(main.gioi_tinh[i]);
    if (!gioi_tinh) return acc;

    acc.push({
      chuc_vu: parse.string(main.chuc_vu?.[i]),
      cong_no_tam_ung: parse.boolean(main.cong_no_tam_ung?.[i], true),
      dia_chi: parse.string(main.dia_chi?.[i]),
      dien_thoai: parse.string(main.dien_thoai?.[i]),
      email: parse.string(main.email?.[i]),
      gioi_tinh,
      ho_ten_nhan_vien,
      ma_bo_phan: getUUID(uuid, 'ma_bp', i) || null,
      ma_nhan_vien,
      ngay_hieu_luc_cmnd: parse.date(main.ngay_hieu_luc_cmnd?.[i]),
      ngay_sinh: parse.date(main.ngay_sinh?.[i]),
      nhan_vien_ban_hang: parse.boolean(main.nhan_vien_ban_hang?.[i], true),
      nhan_vien_mua_hang: parse.boolean(main.nhan_vien_mua_hang?.[i], true),
      noi_cap_cmnd: getUUID(uuid, 'noi_cap_cmnd', i) || null,
      so_cmnd: parse.string(main.so_cmnd?.[i]),
      noi_sinh: parse.string(main.noi_sinh?.[i])
    });

    return acc;
  }, []);
};

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};

export type { ImportBody };
